import { useState } from "react";
import classNames from "classnames";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import advanced from "dayjs/plugin/advancedFormat";
import { BasicTable } from "../../../atoms/BasicTable";
import { Label } from "../../../atoms/Label";
import { Typography } from "components/atoms";
import { DrillModal } from "components/organisms/TabClassHomeWork";
import "./tests-details-table.scss";
import { useSegmentReFetch } from "context";
import {
  homeApi,
  useGetCourseworkQuery,
  useGetTestScoresAndPerformanceQuery,
} from "services/homeApi";
import { getObjectKeysByValue } from "services/helpers";
import { getTimeFilterInput } from "utils/functions/getTimeFilterInput";
import {
  reStructureSegment,
  replaceArrayOfObjects,
} from "utils/functions/courseWorkReStructure";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "components/molecules/ToolTip";
import { getTooltipContentByStatus } from "utils/functions/tooltipContentByStatus";
import { useLazyGetStudyPlannerEventsQuery } from "services/studyPlanner";
 
import { RefreshModal } from "components/molecules/RefreshModal";
import { useLazyGetAiRecommendationsQuery } from "services/aiRecommendations";
import { decodeToken } from "utils/functions/decodeToken";
import { UnlockModal } from "components/organisms/SelfPacedHomeworkTab/UnlockModal";
import {
  replaceSelfPacedArrayOfObjects,
  reStructureSelfPacedSegment,
} from "utils/functions/selfPacedCourseWorkRestructure";
import { DASHBOARD_PROGRAME, MCAT_TEST_TYPE, SELF_PACED_PROGRAME } from "utils/constants";
import { useAppDispatch } from "hooks";
import { selfPacedHomeApi } from "services/selfPacedHomeApi";
import { practiceApi } from "services/practice";
import { useGetPracticeTestsQuery, useGetTestResultsQuery } from "services/practice";
import { isMCATExternalContent, openExternalUrl } from "utils/functions/openExternalUrl";

interface TestData {
  testName?: string;
  Name?: string;
  dueDate: string;
  ClassDate?: string;
  DateCompleted?: string;
  completedOn: string;
  TimeCompleted?: string;
  Status: string;
}

interface IProps {
  theme: "light" | "dark";
  data?: any;
  type?: string;
  isFetching?: boolean;
  refetch?: any;
  courseIndexIsFetching?: boolean;
  courseIndexRefetch?: any;
  isTableBodyScrolled?: boolean;
  onParentModalClose?: () => void;
}

interface tableData {
  Name: any;
  DueDate: any;
  DateCompleted: any;
  Status: any;
  LaunchUrl: any;
  ContentObjectType?: any;
  ContentObjectGuid?: any;
  SubjectSectionName?: any;
}

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(advanced);
const guessedTimezone = dayjs.tz.guess();

export const TestsDetailsTable = ({
  data,
  type,
  isFetching,
  refetch,
  theme,
  courseIndexIsFetching,
  courseIndexRefetch,
  isTableBodyScrolled,
  onParentModalClose,
}: IProps) => {
  const [contentObjectType, setContentObjectType] = useState<
    string | undefined
  >("");

  const payload = decodeToken();
  const dispatch = useAppDispatch();

  const isDashboard =
    payload &&
    payload.hasOwnProperty("programtype") &&
    payload.programtype === DASHBOARD_PROGRAME;

  const isSelfPaced =
    payload &&
    payload.hasOwnProperty("programtype") &&
    payload.programtype === SELF_PACED_PROGRAME;

  const testtype =
    payload && payload.hasOwnProperty("testtype") && payload.testtype;

  const isMCAT = testtype === MCAT_TEST_TYPE;
  const isAiEnabled =
    !!payload &&
    typeof payload.showaitutor === "string" &&
    payload.showaitutor.toLowerCase() === "true";
  const [courseWorkPageNo, setCourseWorkPageNo] = useState(1);
  const {
    data: courseWorkData,
    setData,
    selectedBtn,
    selectedSession,
    subject,
  } = useSegmentReFetch();

  const { refetch: courseWorkRefetch, isFetching: courseWorkIsFetching } =
    useGetCourseworkQuery({
      page: courseWorkPageNo,
      ...(isDashboard
        ? {
            selectedOption: parseInt(
              getObjectKeysByValue(selectedBtn, true)[0],
              10
            ),
          }
        : {}),
      ...(isDashboard
        ? {
            timeFilter:
              selectedSession === null
                ? 1
                : getTimeFilterInput(Object.values(selectedSession)[0]),
          }
        : {}),
      ...(isSelfPaced && testtype === MCAT_TEST_TYPE
        ? {
            subject: subject,
          }
        : {}),
    });

  const {
    refetch: testScoresAndPerformanceRefetch,
    isFetching: testScoresAndPerformanceIsfetching,
  } = useGetTestScoresAndPerformanceQuery();

  const [triggerRefetchAiRecommendation, aiRecommendationRefetchResult] =
    useLazyGetAiRecommendationsQuery();

  const [triggerStudyPlannerRefetch, studyPlannerRefetchResult] =
    useLazyGetStudyPlannerEventsQuery();

  // Practice-related queries to ensure practice tabs reflect status changes
  const { refetch: practiceTestsRefetch, isFetching: practiceTestsIsFetching } =
    useGetPracticeTestsQuery(undefined, { skip: !payload });
  const { refetch: practiceResultsRefetch, isFetching: practiceResultsIsFetching } =
    useGetTestResultsQuery(undefined, { skip: !payload });

  const [openUpdateModalState, setOpenUpdateModalState] = useState<any>({
    isOpen: false,
    data: null,
  });

  const [toggleUnlockModal, settoggleUnlockModal] = useState(false);
  const [isRefreshModalOpen, setIsReFreshModalOpen] = useState({
    isOpen: false,
    data: "",
  });

  const handleUpgradeCourse = () => {
    if (payload && payload.hasOwnProperty("freeToPaidUpgradeLink")) {
      window.open(payload.freeToPaidUpgradeLink, "__blank");
      setTimeout(() => {
        settoggleUnlockModal(false);
        setIsReFreshModalOpen((prev: any) => ({
          ...prev,
          isOpen: true,
          data: "Purchase",
        }));
      }, 500);
    }
  };

  const handleRefreshModalClose = () => {
    setIsReFreshModalOpen((prev) => ({ ...prev, isOpen: false, data: "" }));
  };

  const handleRefreshModalOpen = (obj: any) => {
    setContentObjectType((prev) => obj.ContentObjectType || obj.Type);
    setIsReFreshModalOpen((prev) => ({ ...prev, isOpen: true }));
  };

  const handleRefresh = async () => {
    const promises = [
      courseWorkRefetch && courseWorkRefetch(),
      refetch && refetch(),
      courseIndexRefetch && courseIndexRefetch(),
      testScoresAndPerformanceRefetch && testScoresAndPerformanceRefetch(),
      triggerRefetchAiRecommendation && triggerRefetchAiRecommendation(),
      practiceTestsRefetch && practiceTestsRefetch(),
      practiceResultsRefetch && practiceResultsRefetch(),
      ...(isDashboard ? [triggerStudyPlannerRefetch()] : []),
    ];

    Promise.allSettled(promises).then((responses: any) => {
      if (responses[0].status === "fulfilled") {
        if (isDashboard) {
          const deStructuredArray = reStructureSegment(responses[0].value.data);
          const updatedArray = replaceArrayOfObjects(
            deStructuredArray,
            courseWorkData
          );
          setData([...updatedArray]);
        } else if (isSelfPaced) {
          const deStructuredArray = reStructureSelfPacedSegment(
            responses[0].value.data
          );
          const updatedArray = replaceSelfPacedArrayOfObjects(
            deStructuredArray,
            courseWorkData
          );
          setData([...updatedArray]);
        }
      }
      handleRefreshModalClose();
    });
    // Ensure other pages that rely on these queries also refresh
    dispatch(homeApi.util.invalidateTags(["Coursework", "OverallProgress"]));
    dispatch(practiceApi.util.invalidateTags(["Practice"]));
    if (isSelfPaced) {
      dispatch(
        selfPacedHomeApi.util.invalidateTags([
          "SelfPacedCoursework",
          "SelfPacedOverallProgress",
        ])
      );
    }
  };

  const handleClick = (columnName: any, type: any, obj: any) => {
    if (obj.hasOwnProperty("Status") && obj.Status.toLowerCase() === "locked") {
      settoggleUnlockModal(true);
    } else if (columnName === "Name" && type !== "attendance") {
      if (
        obj.ContentObjectType === "OfflineDrill" ||
        obj.ContentObjectType === "OfflineReading"
      ) {
        setOpenUpdateModalState((prev: any) => ({
          ...prev,
          isOpen: true,
          data: obj,
        }));
      } else {
        if (
          isMCAT &&
          isAiEnabled &&
          onParentModalClose &&
          !isMCATExternalContent(obj.ContentObjectType)
        ) {
          onParentModalClose();
        }
        const testtype = payload?.testtype;
        openExternalUrl({
          url: obj["LaunchUrl"],
          contentObjectType: obj.ContentObjectType,
          testType: testtype,
          dispatch,
          onRefreshModalOpen: () => {
            handleRefreshModalOpen(obj);
          },
        });
      }

      if (type === "homework") {
        const selectedSegment =
          courseWorkData &&
          Array.isArray(courseWorkData) &&
          courseWorkData.filter((item) =>
            isDashboard
              ? item.Description === obj["SubjectSectionName"]
              : item.LearningTrackName === obj["SubjectSectionName"]
          );

        if (
          selectedSegment &&
          Array.isArray(selectedSegment) &&
          selectedSegment[0] &&
          selectedSegment[0].page
        ) {
          setCourseWorkPageNo((prev) => selectedSegment[0].page);
        }
      }
      if (type === "Diagnostics") {
        const selectedDiagSegment =
          courseWorkData &&
          Array.isArray(courseWorkData) &&
          courseWorkData.filter((item) =>
            isDashboard
              ? item.Description === obj["SubjectSectionName"]
              : item.LearningTrackName === obj["SubjectSectionName"]
          );

        if (
          selectedDiagSegment &&
          Array.isArray(selectedDiagSegment) &&
          selectedDiagSegment[0] &&
          selectedDiagSegment[0].page
        ) {
          setCourseWorkPageNo((prev) => selectedDiagSegment[0].page);
        }
      }
      if (type === "Tests") {
        const selectedTestSegment =
          courseWorkData &&
          Array.isArray(courseWorkData) &&
          courseWorkData.filter(
            (item) =>
              item.IsTestSegment === true &&
              item?.ExamCo?.Name === obj["SubjectSectionName"]
          );

        if (
          selectedTestSegment &&
          Array.isArray(selectedTestSegment) &&
          selectedTestSegment[0] &&
          selectedTestSegment[0].page
        ) {
          setCourseWorkPageNo((prev) => selectedTestSegment[0].page);
        }
      }
    }
  };

  const handleUpdateModalClose = () => {
    setOpenUpdateModalState((prev: any) => ({
      ...prev,
      isOpen: false,
      data: null,
    }));
  };

  const getMatchColor = (status: string) => {
    switch (status) {
      case "Past Due":
      case "Time Limit Exceeded":
      case "Not Attended":
      case "Absent":
      case "Late":
        return "red";
      case "Completed":
      case "Complete":
      case "Attended":
      case "Viewed Recording":
      case "Watched Recording":
        return "green";
      case "In Progress":
      case "In-Progress":
        return "light-yellow";
      case "Locked":
      case "locked":
        return "dark-gray";
      default:
        return "gray";
    }
  };

  const tableBodyData: tableData[] = [];

  if (Array.isArray(data)) {
    if (type === "attendance") {
      data.map((item: any, index: number) => {
        const newData: tableData = {
          Name: item.Name,
          DueDate: dayjs(item.ClassDate).isValid()
            ? dayjs.utc(item.ClassDate).local().format("MM/DD/YY")
            : item.ClassDate,
          DateCompleted: dayjs(item.TimeCompleted).isValid()
            ? `${dayjs
                .utc(item.TimeCompleted)
                .local()
                .format("MM/DD/YYYY hh:mm A")} ${dayjs()
                .tz(guessedTimezone)
                .format("z")}`
            : item.TimeCompleted,
          Status: item.Status,
          LaunchUrl:
            item.LaunchUrl ||
            (item.ContentObject && item.ContentObject.LaunchUrl),
        };
        tableBodyData.push(newData);
      });
    } else {
      data.map((item: any, index: number) => {
        const newData: tableData = {
          Name: item.Name,
          DueDate: dayjs(item.DueDate).isValid()
            ? dayjs.utc(item.DueDate).local().format("MM/DD/YY")
            : item.DueDate,
          DateCompleted: dayjs(item.DateCompleted).isValid()
            ? `${dayjs
                .utc(item.DateCompleted)
                .local()
                .format("MM/DD/YYYY hh:mm A")} ${dayjs()
                .tz(guessedTimezone)
                .format("z")}`
            : item.DateCompleted,
          Status: item.Status,
          LaunchUrl:
            item.LaunchUrl ||
            (item.ContentObject && item.ContentObject.LaunchUrl),
          ContentObjectType: item.ContentObject
            ? item.ContentObject.ContentObjectType
            : item.Type,
          ContentObjectGuid: item.ContentObject
            ? item.ContentObject.ContentObjectGuid
            : "",
          SubjectSectionName:
            type === "homework"
              ? item.SubjectSegmentDesc
              : type === "Diagnostics"
              ? isDashboard
                ? item.SegmentDescription
                : item.Subject
              : type === "Tests"
              ? item.Name
              : "",
        };
        tableBodyData.push(newData);
      });
    }
  }

          return (
    <BasicTable
      rules="none"
      columns={
        type === "attendance"
          ? [
              { title: "Class Name", key: "testName" },
              { title: "Due Date", key: "dueDate" },
              { title: "Date Completed", key: "completedOn" },
              { title: "Status", key: "status" },
            ]
          : type === "Diagnostics"
            ? [
                { title: "Diagnostic Exam Name", key: "testName" },
                { title: "Due Date", key: "dueDate" },
                { title: "Date Completed", key: "completedOn" },
                { title: "Status", key: "status" },
              ]
          : type === "Tests"
          ? [
              { title: "Test Name", key: "testName" },
              { title: "Due Date", key: "dueDate" },
              { title: "Date Completed", key: "completedOn" },
              { title: "Status", key: "status" },
            ]
            : [
                { title: "Homework Name", key: "testName" },
                { title: "Due Date", key: "dueDate" },
                { title: "Date Completed", key: "completedOn" },
                { title: "Status", key: "status" },
              ]
      }
      data={tableBodyData}
      renderHead={(customColumns) => (
        <thead
          className={classNames({
            "tests-details-thead": true,
            "has-scrolled": isTableBodyScrolled,
          })}
        >
          <tr>
            {customColumns.map((obj, index) => (
              <th key={index + obj.title} className={`${obj.key}`}>
                {obj.title}
              </th>
            ))}
          </tr>
        </thead>
      )}
      renderBody={(customData) => (
        <tbody className={classNames("tests-details-tbody")}>
          {customData && customData.length > 0 ? (
            customData.map((obj, index) => (
              <tr
                key={index}
                className={classNames({
                  highlight: index % 2 === 0,
                })}
              >
                {Object.keys(obj)
                  .filter(
                    (value: any) =>
                      value === "Name" ||
                      value === "DueDate" ||
                      value === "ClassDate" ||
                      value === "DateCompleted" ||
                      value === "TimeCompleted" ||
                      value === "Status"
                  )
                  .map((columnName, innerIndex) => (
                    <td
                      key={innerIndex + columnName}
                      className={classNames({
                          "test-name":
                            columnName === "Name" && type !== "attendance",
                          status: columnName === "Status",
                      })}
                      onClick={() => handleClick(columnName, type, obj)}
                    >
                      {columnName === "Status" ? (
                        <Tooltip>
                          <TooltipTrigger>
                            <Label
                              backgroundColor={getMatchColor(
                                obj[columnName as keyof TestData]
                              )}
                              dynamicTextColor={
                                obj[
                                  columnName as keyof TestData
                                ].toLowerCase() === "locked"
                                  ? "#ffffff"
                                  : "#000000"
                              }
                              label={
                                obj[
                                  columnName as keyof TestData
                                ].toLowerCase() === "complete" ||
                                obj[
                                  columnName as keyof TestData
                                ].toLowerCase() === "completed"
                                  ? "Completed"
                                  : obj[columnName as keyof TestData]
                              }
                            />
                          </TooltipTrigger>
                          <TooltipContent className={`status-tooltip-${theme}`}>
                            <Typography
                              variant="h8Book"
                              label={getTooltipContentByStatus(
                                obj[columnName as keyof TestData]
                              )}
                            />
                          </TooltipContent>
                        </Tooltip>
                      ) : columnName === "Name" ||
                        columnName === "DueDate" ||
                        columnName === "ClassDate" ||
                        columnName === "TimeCompleted" ||
                        columnName === "DateCompleted" ? (
                        obj[columnName as keyof TestData]
                      ) : null}
                    </td>
                  ))}
              </tr>
            ))
          ) : (
            <div className="empty-data-wrapper">
              <Typography variant="h8Book" label="No data available" />
            </div>
          )}
          <RefreshModal
            isOpen={isRefreshModalOpen.isOpen}
            handleOnClose={handleRefreshModalClose}
            theme={theme}
            contentObjectType={
              isRefreshModalOpen.data !== ""
                ? isRefreshModalOpen.data
                : contentObjectType
            }
            isFetching={
              isFetching ||
              courseIndexIsFetching ||
              courseWorkIsFetching ||
              testScoresAndPerformanceIsfetching ||
            practiceTestsIsFetching ||
            practiceResultsIsFetching ||
              studyPlannerRefetchResult.isFetching ||
              aiRecommendationRefetchResult.isFetching
            }
            handleRefresh={handleRefresh}
          />
          <DrillModal
            handleUpdateModalClose={handleUpdateModalClose}
            openUpdateModalState={openUpdateModalState}
            theme={theme}
            isFetchingOtherAPIs={
              isFetching ||
              courseIndexIsFetching ||
              courseWorkIsFetching ||
              testScoresAndPerformanceIsfetching ||
              studyPlannerRefetchResult.isFetching ||
              aiRecommendationRefetchResult.isFetching
            }
            refetchData={handleRefresh}
          />
          <UnlockModal
            handleUnlockModalClose={() => settoggleUnlockModal(false)}
            handleUpgrade={handleUpgradeCourse}
            unlockModalState={toggleUnlockModal}
            theme={theme}
          />
        </tbody>
      )}
    />
  );
};
